import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AnimatedIcon } from '../../components/icons';
import { proposalService } from '../../services/proposalService';
import { useAuthStore } from '../../store/authStore';
import { useRequestStore } from '../../store/requestStore';
import { RequestState } from '../../types';
import { isSurveyorApproved } from '../../utils/surveyorApproval';
import { WEB_STATUS_COLORS } from '../../utils/statusPresentation';

const STATUS_COLORS = WEB_STATUS_COLORS;

export const SurveyorRequestDetail: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { currentRequest: req, fetchRequestDetail, isLoading, error } = useRequestStore();
  const { user } = useAuthStore();
  const [bidAmount, setBidAmount] = useState('');
  const [bidMessage, setBidMessage] = useState('');
  const [isSubmittingBid, setIsSubmittingBid] = useState(false);
  const [bidError, setBidError] = useState<string | null>(null);
  const [bidSuccess, setBidSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (requestId) fetchRequestDetail(requestId);
  }, [requestId]);

  if (isLoading && !req) {
    return (
      <div style={s.center}>
        <div style={s.spinner} />
        <p style={s.muted}>Loading job...</p>
      </div>
    );
  }

  if (error || !req) {
    return (
      <div style={s.center}>
        <AnimatedIcon name="warning" size={44} color="var(--sc-error)" />
        <p style={{ ...s.muted, color: 'var(--sc-error)' }}>{error || 'Job not found'}</p>
        <button style={s.backBtn} onClick={() => navigate(-1)}>Back</button>
      </div>
    );
  }

  const sc = STATUS_COLORS[req.status] || STATUS_COLORS.REQUEST_SUBMITTED;
  const hasDeal = Boolean(req.deal_id);
  const canOpenBid = req.status === RequestState.APPROVED && !hasDeal;
  const canBid = canOpenBid && isSurveyorApproved(user);

  const handleSubmitBid = async () => {
    const amount = Number(bidAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setBidError('Enter the amount you want to bid for this job.');
      return;
    }

    setIsSubmittingBid(true);
    setBidError(null);
    setBidSuccess(null);
    try {
      const proposal = await proposalService.create({
        survey_request: req.id,
        proposed_price: amount,
        message: bidMessage.trim() || undefined,
      });
      await fetchRequestDetail(req.id);
      setBidSuccess('Offer sent to the client chat for approval. A deal will be created after the client accepts it.');
      if (proposal.deal_id) {
        navigate(`/surveyor/deal/${proposal.deal_id}`, { replace: true });
      } else if (proposal.chat_room_id) {
        navigate(`/surveyor/chat/room/${proposal.chat_room_id}`, { replace: true });
      }
    } catch (err: any) {
      setBidError(
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.message ||
        'Unable to submit your bid.',
      );
    } finally {
      setIsSubmittingBid(false);
    }
  };

  return (
    <div style={s.page}>
      <button style={s.backLink} onClick={() => navigate(-1)}>Back to jobs</button>

      <div style={s.header}>
        <div>
          <h1 style={s.title}>{req.service_type}</h1>
          <p style={s.subtitle}>Job #{req.id.slice(0, 8).toUpperCase()}</p>
        </div>
        <span style={{ ...s.statusBadge, background: sc.bg, color: sc.color, borderColor: sc.border }}>
          {req.status.replace(/_/g, ' ')}
        </span>
      </div>

      <div style={s.grid}>
        <section style={s.card}>
          <h2 style={s.cardTitle}>Job Details</h2>
          <Field label="Description" value={req.description} />
          <Field label="Location" value={req.address || 'Location unavailable'} />
          <Field label="Coordinates" value={`${req.latitude}, ${req.longitude}`} />
          <Field label="Deadline" value={req.deadline ? new Date(req.deadline).toLocaleDateString('en-ZW') : 'Not set'} />
          <Field label="Posted" value={new Date(req.created_at).toLocaleDateString('en-ZW')} />
        </section>

        <aside style={s.card}>
          <h2 style={s.cardTitle}>Client</h2>
          <div style={s.clientRow}>
            <div style={s.avatar}>
              {(req.client?.first_name?.[0] || '?').toUpperCase()}
              {(req.client?.last_name?.[0] || '').toUpperCase()}
            </div>
            <div>
              <div style={s.clientName}>{req.client?.first_name} {req.client?.last_name}</div>
              <div style={s.muted}>Contact unlocks after confirmation.</div>
            </div>
          </div>

          {hasDeal ? (
            <Link to={`/surveyor/deal/${req.deal_id}`} style={s.primaryBtn}>
              Open Deal
            </Link>
          ) : canOpenBid && !canBid ? (
            <div style={s.notice}>
              Your account is waiting for admin approval. You can browse jobs, but bidding is enabled only after approval.
            </div>
          ) : canBid ? (
            <div style={s.bidForm}>
              <label style={s.inputLabel}>Bid Amount</label>
              <input
                value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value)}
                placeholder="e.g. 250"
                inputMode="decimal"
                style={s.input}
              />
              <label style={s.inputLabel}>Message</label>
              <textarea
                value={bidMessage}
                onChange={(e) => setBidMessage(e.target.value)}
                placeholder="Add timing, scope notes, or requirements..."
                style={{ ...s.input, minHeight: 92, resize: 'vertical' }}
              />
              {bidError && <div style={s.errorText}>{bidError}</div>}
              {bidSuccess && <div style={{ ...s.notice, color: 'var(--sc-success-text)', borderColor: 'var(--sc-success)' }}>{bidSuccess}</div>}
              <button
                style={{ ...s.primaryBtn, border: 'none', cursor: isSubmittingBid ? 'default' : 'pointer', opacity: isSubmittingBid ? 0.65 : 1 }}
                onClick={handleSubmitBid}
                disabled={isSubmittingBid}
              >
                {isSubmittingBid ? 'Submitting Bid...' : 'Submit Bid'}
              </button>
            </div>
          ) : (
            <div style={s.notice}>
              This job has not been assigned into a deal yet.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={s.field}>
    <span style={s.fieldLabel}>{label}</span>
    <span style={s.fieldValue}>{value}</span>
  </div>
);

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 960, margin: '0 auto', paddingBottom: 40 },
  center: { minHeight: 320, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  muted: { color: 'var(--sc-text-secondary)', fontSize: 13, margin: 0 },
  backLink: { border: 'none', background: 'transparent', color: 'var(--sc-brand-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 16, fontFamily: 'inherit' },
  backBtn: { border: '1px solid var(--sc-border)', background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-primary)', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontWeight: 700, fontFamily: 'inherit' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 22 },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px' },
  subtitle: { fontSize: 13, color: 'var(--sc-text-tertiary)', margin: 0, fontWeight: 600 },
  statusBadge: { border: '1px solid', borderRadius: 7, padding: '6px 12px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase' },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(280px, 0.8fr)', gap: 16 },
  card: { background: 'var(--sc-bg-secondary)', border: '1px solid var(--sc-border)', borderRadius: 12, padding: 22, boxShadow: 'var(--sc-shadow-sm)' },
  cardTitle: { fontSize: 16, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 16px' },
  field: { display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 },
  fieldLabel: { fontSize: 10, fontWeight: 800, color: 'var(--sc-text-tertiary)', textTransform: 'uppercase' },
  fieldValue: { fontSize: 14, color: 'var(--sc-text-primary)', lineHeight: 1.45 },
  clientRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  avatar: { width: 44, height: 44, borderRadius: 12, background: 'var(--sc-brand-primary)', color: 'var(--sc-text-on-brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 },
  clientName: { fontSize: 14, fontWeight: 800, color: 'var(--sc-text-primary)' },
  primaryBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 42, borderRadius: 9, background: 'var(--sc-brand-primary)', color: 'var(--sc-text-on-brand)', textDecoration: 'none', fontSize: 14, fontWeight: 800 },
  bidForm: { display: 'flex', flexDirection: 'column', gap: 8 },
  inputLabel: { fontSize: 11, fontWeight: 800, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' },
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid var(--sc-border)', background: 'var(--sc-bg-primary)', color: 'var(--sc-text-primary)', borderRadius: 8, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit' },
  errorText: { color: 'var(--sc-error)', fontSize: 12, fontWeight: 700 },
  notice: { background: 'var(--sc-warning-bg)', border: '1px solid var(--sc-warning)', color: 'var(--sc-warning-text)', borderRadius: 9, padding: 12, fontSize: 13, lineHeight: 1.45 },
};
